import React, { useState, useMemo } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import { useStatusPalette } from "../../../utils/statusColors";
import { formatMessageTime, parseFormSubmissionAnswers } from "./conversationUtils";

export function SystemMessage({ content }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", px: 2.5 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          backgroundColor: "action.hover",
          borderRadius: "8px",
          px: 2,
          py: 0.75,
          cursor: "pointer",
          width: "100%",
          transition: "background-color 150ms ease",
          "&:hover": { backgroundColor: "action.selected" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "12px", color: "text.secondary", flex: 1 }}>
            System prompt
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 14,
              color: "text.disabled",
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
        <Collapse in={expanded}>
          <Typography
            sx={{
              mt: 1,
              fontSize: "12px",
              fontFamily: "monospace",
              color: "text.primary",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
            }}
          >
            {content}
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
}

export function HumanTakeoverMarker({ marker, timestamp }) {
  const _sp = useStatusPalette();
  const isStart = marker?.kind === "start";
  const _t = isStart ? _sp.tealAlt : _sp.cyan;
  const tone = { bg: _t.bg, border: _t.border, text: _t.color };
  return (
    <Box sx={{ px: 2.5, py: 0.75, display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: "999px",
          border: `1px solid ${tone.border}`,
          backgroundColor: tone.bg,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          maxWidth: "100%",
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: tone.text,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            whiteSpace: "nowrap",
          }}
        >
          {isStart ? "Human takeover started" : "Human takeover ended"}
        </Typography>
        {timestamp && (
          <Typography sx={{ fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap" }}>
            {formatMessageTime(timestamp)}
          </Typography>
        )}
        {marker?.message && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {marker.message}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ContextCompactedMarker({ marker, timestamp }) {
  const _sp = useStatusPalette();
  const tone = { bg: _sp.purple.bg, border: _sp.purple.border, text: _sp.purple.color };
  return (
    <Box sx={{ px: 2.5, py: 0.75, display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: "999px",
          border: `1px solid ${tone.border}`,
          backgroundColor: tone.bg,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          maxWidth: "100%",
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: tone.text,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            whiteSpace: "nowrap",
          }}
        >
          Context summarized
        </Typography>
        {timestamp && (
          <Typography sx={{ fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap" }}>
            {formatMessageTime(timestamp)}
          </Typography>
        )}
        {marker?.message && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {marker.message}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function FormSubmissionMessage({ content, timestamp, senderName, isReview }) {
  const _sp = useStatusPalette();
  const parsed = useMemo(() => parseFormSubmissionAnswers(content), [content]);
  const displayName = senderName || "Someone";
  const tone = isReview ? _sp.purple : _sp.blue;
  const Icon = isReview ? SupervisorAccountOutlinedIcon : AssignmentTurnedInOutlinedIcon;
  const label = isReview
    ? `${displayName} completed a review`
    : `${displayName} submitted a form`;

  return (
    <Box sx={{ px: 2.5, py: 0.75 }}>
      <Box
        sx={{
          borderRadius: "10px",
          border: `1px solid ${tone.border}`,
          backgroundColor: tone.bg,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: parsed?.answers?.length
              ? `1px solid ${tone.border}`
              : "none",
          }}
        >
          <Icon
            sx={{ fontSize: 16, color: tone.color, flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: tone.color,
              flex: 1,
            }}
          >
            {label}
          </Typography>
          {timestamp && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {formatMessageTime(timestamp)}
            </Typography>
          )}
        </Box>
        {parsed?.answers?.length > 0 && (
          <Box sx={{ px: 1.5, py: 1 }}>
            {parsed.answers.map((a, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  gap: 1,
                  py: 0.3,
                  alignItems: "baseline",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "text.secondary",
                    flexShrink: 0,
                    minWidth: 0,
                  }}
                >
                  {a.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "text.primary",
                    fontWeight: 500,
                    wordBreak: "break-word",
                  }}
                >
                  {a.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {(!parsed || !parsed.answers?.length) && content && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {content}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
